import { useCallback, useEffect, useState } from "react";

import type { TaskLogEntry } from "../types";

import { getTaskLogsAsUser } from "../dashboard-client";
import { formatErrorMessage } from "../utils/errors";

export interface TaskLogsResult {
  data: TaskLogEntry[] | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface TaskLogsState {
  data: TaskLogEntry[] | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: TaskLogsState = {
  data: null,
  error: null,
  loading: false,
};

export function useTaskLogs(taskId: string, enabled: boolean): TaskLogsResult {
  const [state, setState] = useState<TaskLogsState>(INITIAL_STATE);

  const load = useCallback(
    async (force = false) => {
      setState((current) => ({
        data: force ? null : current.data,
        error: null,
        loading: true,
      }));

      try {
        const logsResult = await getTaskLogsAsUser({ path: { taskId } });
        const entries = logsResult.data.logs;
        setState({ data: entries, error: null, loading: false });
      } catch (error) {
        setState((current) => ({
          data: current.data,
          error: formatErrorMessage(error),
          loading: false,
        }));
      }
    },
    [taskId],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (state.data || state.loading) {
      return;
    }
    void load(false);
  }, [enabled, state.data, state.loading, load]);

  const reload = useCallback(() => {
    void load(true);
  }, [load]);

  return {
    data: state.data,
    error: state.error,
    loading: state.loading,
    reload,
  };
}
